import connect from 'Shared/connect';
import { isCompact as functionIsCompact } from 'Shared/Viewport';
import { styled } from '@glitz/react';
import { Left32x32, Right32x32 } from 'Shared/Icons/ArrowCircle';
import { TextBlock, TextLabel, ResponsiveSize } from 'Shared/SharedComponents/atoms/Typography';
import { translate } from '@polarnopyret/scope/index';
import React from 'react';
import * as style from 'Shared/Style';

const SHOW_ALL_PAGES = 6

const PagingWrap = styled.div({
  marginTop: '80px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
})

const PagingInner = styled.div({
  display: 'flex',
  flexDirection: 'row',
  ['@media ' + style.mediaUpToLarge]: {
    width: '100%'
  },
  ['@media ' + style.mediaMinLarge]: {
    gap: '32px'
  },
})

const PagingLeftWrap = styled.div({
  ['@media ' + style.mediaUpToLarge]: {
    flexGrow: 1,
  },
})

const PagingNumberWrap = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: '24px',
  ['@media ' + style.mediaMinLarge]: {
    gap: '16px',
  },
})

const PagingRightWrap = styled.div({
  ['@media ' + style.mediaUpToLarge]: {
    flexGrow: 1,
    textAlign: 'right'
  },
})

const Pagination = connect((state) => ({
  currentBreakpoint: state.currentBreakpoint,
}))(
  (props: {
    pageCount: number;
    currentPage: number;
    totalItemCount: number;
    totalItemCountTextColor: string;
    currentlyDisplayedItemCount: number;
    navigateToPage: (page: number) => void;
    currentBreakpoint: number;
    totalItemCountLabel?: string;
    inactiveColorAlpha?: number;
  }) => {
    const { pageCount, currentPage, navigateToPage, totalItemCount, currentlyDisplayedItemCount, totalItemCountLabel, totalItemCountTextColor, inactiveColorAlpha = 0.6 } =
      props;
    const isCompact = functionIsCompact(props.currentBreakpoint);
    const secondaryColor = style.hex2rgba(style.colors.monochrome.black, inactiveColorAlpha);
    
    let pages: string[] = []
    for (let i = 0; i < pageCount; i++) {
      const pageNum = i + 1
      const lastPageNumInArr: String | undefined = pages[pages.length - 1]

      if (pageCount <= SHOW_ALL_PAGES) {
        pages.push(String(pageNum))
        continue;
      }

      if (pageNum === 1) {
        // First page, add this - Always visible
        pages.push(String(pageNum))
        continue;
      }

      if (pageNum === pageCount) {
        // Last page, add this - Always visible
        pages.push(String(pageNum))
        continue
      }

      if (pageNum === currentPage - 1 && pageNum + 3 !== pageCount) {
        pages.push(String(pageNum))
        continue
      }

      if (pageNum === currentPage + 1 && pageNum - 3 !== 1) {
        pages.push(String(pageNum))
        continue        
      }

      if (currentPage === 1 && pageNum === 3) {
        pages.push(String(pageNum))
        continue        
      }

      if (pageNum === currentPage) {
        pages.push(String(pageNum))
        continue
      }

      if (lastPageNumInArr !== undefined && lastPageNumInArr !== '...') {
        pages.push('...')
        continue
      }
    }

    return (
      <PagingWrap>
        {pageCount > 1 && 
          <PagingInner>
            <PagingLeftWrap>
              <Left32x32
                css={{
                  cursor: currentPage === 1 ? undefined : 'pointer',
                  color:
                    currentPage === 1
                      ? secondaryColor
                      : style.colors.monochrome.black,
                }}
                onClick={() => {
                  if (currentPage !== 1) {
                    navigateToPage(currentPage - 1);
                  }
                }}
              />
            </PagingLeftWrap>
            <PagingNumberWrap>
              {pages.map((page, idx) => {
                if (page === '...') {
                  return <styled.Div
                    css={{
                      color: `${style.colors.rgbaFilters.black50}`,
                    }}
                    key={idx}
                    >
                    <TextBlock fontSize={ResponsiveSize.D32_M32}>...</TextBlock>
                  </styled.Div>
                }

                return <styled.Div
                  onClick={() => navigateToPage(Number(page))}
                  key={idx}
                  css={{
                    color:
                      currentPage === Number(page)
                        ? `${style.colors.monochrome.black}`
                        : `${style.colors.rgbaFilters.black50}`,
                    cursor: 'pointer',
                  }}
                  >
                  <TextBlock fontSize={ResponsiveSize.D32_M32}>{page}</TextBlock>
                  </styled.Div>
              })}
            </PagingNumberWrap>
            <PagingRightWrap>
              <Right32x32
                css={{
                  cursor: currentPage === pageCount ? undefined : 'pointer',
                  color:
                    currentPage === pageCount
                      ? secondaryColor
                      : style.colors.monochrome.black,
                }}
                onClick={() => {
                  if (currentPage !== pageCount) {
                    navigateToPage(currentPage + 1);
                  }
                }}
              />
            </PagingRightWrap>
          </PagingInner>
        }
        <TextLabel color={totalItemCountTextColor}>
          {totalItemCountLabel ?? translate('/site/shared/displaynritems', currentlyDisplayedItemCount, totalItemCount)}
        </TextLabel>
      </PagingWrap>
    );
  },
);

export default Pagination;
